import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
} from 'reactstrap';
import { Formik, useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import CreatableSelect from 'react-select/creatable';
import 'react-quill/dist/quill.snow.css';

// Import advanced components
import Select from 'react-select';
import Switch from 'react-switch';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import { uploadImageFile, uploadDocumentFile, saveDocumentMetadata, fetchParentCategories, fetchSubCategories } from '../../../slices/thunks';
import { useTranslation } from 'react-i18next';
import UploadProgressModal from '../../../Components/Common/UploadProgressModal';
import { getAuthUser } from '../../../helpers/utils';


// Register FilePond plugins
registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview);

// Helper function to convert a File object to a Base64 string
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = (error) => reject(error);
});

export const getCustomSelectStyles = (isInvalid, isRTL) => ({
  control: (base, state) => ({
    ...base,
    borderColor: isInvalid ? '#f46a6a' : state.isFocused ? '#80bdff' : '#ced4da',
    '&:hover': { borderColor: isInvalid ? '#f46a6a' : '#adb5bd' },
    boxShadow: isInvalid
      ? '0 0 0 0.2rem rgba(244, 106, 106, 0.25)'
      : state.isFocused
      ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)'
      : 'none',
    direction: isRTL ? 'rtl' : 'ltr',
  }),
  menu: base => ({ ...base, textAlign: isRTL ? 'right' : 'left' }),
  multiValue: base => ({ ...base, direction: isRTL ? 'rtl' : 'ltr' }),
});

const MAX_FILE_SIZE = 52428800; // 50 MB

const AddBook = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = getAuthUser();
  const isRTL = i18n.dir() === "rtl";
  const [submitAction, setSubmitAction] = useState('');
  
  // --- STATE TO HOLD UPLOADED FILE URLS ---
  const [uploadedCoverUrl, setUploadedCoverUrl] = useState(null);
  const [uploadedPdfData, setUploadedPdfData] = useState(null);
  
  // State to disable elements during individual uploads
  const [isCoverUploading, setIsCoverUploading] = useState(false);
  const [isPdfUploading, setIsPdfUploading] = useState(false);

  // <-- NEW: State for the progress log modal
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [isLogModalMinimized, setIsLogModalMinimized] = useState(false);
  const [uploadLogs, setUploadLogs] = useState([]);


  const filePondPdfRef = useRef(null);
  const filePondImageRef = useRef(null);

  const toggleLogModal = () => setIsLogModalOpen(!isLogModalOpen);
  const toggleLogModalMinimize = () => setIsLogModalMinimized(!isLogModalMinimized);


  // --- NEW: SELECT DATA FROM REDUX STORE ---
  const { 
    parentCategories, 
    loading: categoriesLoading 
  } = useSelector((state) => state.Category);
  
  const { 
    subCategories, 
    subCategoriesLoading,
    uploadProgress, 
  } = useSelector((state) => state.AIDoc); 

  const handleReset = () => {
    formik.resetForm();
    // setUploadedPdfData(null);
    // setUploadedCoverUrl(null);
    // toggleLogModal();
    if (filePondPdfRef.current) {
      filePondPdfRef.current.removeFiles();
    }
    if (filePondImageRef.current) {
      filePondImageRef.current.removeFiles();
    }
  };
  
  // --- VALIDATION SCHEMA NOW CHECKS FOR THE UPLOADED URLS ---
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required('BookNameRequired')
      .min(3, 'Must be more than 3 characters')
      .max(50, 'Must be 50 characters or less'),
    author: Yup.string()
      .trim()
      .required('Author name is required')
      .min(3, 'Must be more than 3 characters')
      .max(100, 'Must be 300 characters or less'),
    year: Yup.number()
      .required('Publication year is required')
      .min(1900, 'Must be after 1900')
      .max(new Date().getFullYear(), 'YearCannotBeFuture'), 
    publisher: Yup.string().trim().required('Publisher is required').min(3, 'Must be more than 3 characters').max(100, 'Must be 300 characters or less'),
    // category: Yup.object().required('Category is required').nullable(),
    subCategory: Yup.array()
      .min(1, 'Sub category is required')
      .of(
        Yup.object().shape({
          value: Yup.number().required(),
          label: Yup.string().required(),
        })
    ),
    isPublic: Yup.boolean(),
    // uploadedPdfData: Yup.object().nullable(),
    // uploadedPdfData: Yup.object().required('Please upload a PDF file and wait for it to process.').nullable(),
    // --- CHANGE: Make cover image optional by not including it here, or make it required like this:
    // uploadedCoverUrl: Yup.string().required('Please upload a cover image.').nullable(),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      author: '',
      publisher: '',
      year: '',
      category: null,
      subCategory: null,
      isPublic: true,
      uploadedCoverUrl: null, 
      uploadedPdfData: null,
    },
    validationSchema: validationSchema,
    // --- ONSUBMIT IS NOW SIMPLER ---
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      
      // try {
      //   const finalPayload = {
        //     DocumentTypeID: 5,  // Assuming 5 is the DocumentTypeID for Book
        //     FileName: values.name,
        //     FileType: 'application/pdf', 
        //     ThumbnailURL: values.uploadedCoverUrl,
        //     FileURL: values.uploadedPdfData.uploadUrl,
        //     // CategoryID: parentCategories[0]?.ID,
      //     SubCategoryIDs: Array.isArray(values.subCategory)
      //       ? values.subCategory.map(sc => sc.value)
      //     : [],
      //     UniqueID: values.uploadedPdfData.uniqueId || null,
      //     IsVisible: values.isPublic,
      //     Details: JSON.stringify({
        //       name: values.name,
        //       author: values.author,
        //       publisher: values.publisher,
        //       year: values.year,
      //     })
      //   };
      
      //   await dispatch(saveDocumentMetadata(finalPayload));
      
      //   if (submitAction === 'addAnother') {
        //     resetForm();
        //     // Manually reset file states
        //     // setUploadedCoverUrl(null);
        //     // setUploadedPdfData(null);
        //     handleReset();
        //     // You might need a ref to FilePond to call .removeFiles()
        //   } else {
      //     navigate(-1); 
      //   }
      
      // } catch (error) {
      //   console.error("Save metadata failed:", error);
      //   toast.error(error.message || "Could not save book details.");
      // } finally {
        //   setSubmitting(false);
        // }
      },
    });

  // --- UPLOAD HANDLER FOR THE COVER IMAGE FILEPOND ---
  const processCoverImage = async (fieldName, file, metadata, load, error, progress, abort) => {
    setIsCoverUploading(true);
    progress(true, 0, 1024); 

    try {
        // 2. Prepare the data for the API call.
        let base64String = await fileToBase64(file);
        base64String = base64String.replace(/^data:image\/\w+;base64,/, '');

        const formData = {
            ImageData: base64String,
            folderName: 'BookCovers'
        };

        // 3. Dispatch the thunk and call .unwrap().
        const imageUrl = await dispatch(uploadImageFile(formData)).unwrap();

        // 4. After a successful unwrap, perform a final validation.
        // This handles cases where the API returns 200 OK but with an empty or invalid body.
        if (!imageUrl || typeof imageUrl !== 'string') {
            throw new Error(t('InvalidImageServerErr', { defaultValue: 'Invalid image response from server' }));
        }

        // 5. If all checks pass, this is a true success. Update state and UI.
        // setUploadedCoverUrl(imageUrl);
        formik.setFieldValue('uploadedCoverUrl', imageUrl);
        // toast.success(t('BookCoverImageSuccess', { defaultValue: 'Book cover image uploaded successfully' }));

        // Tell FilePond the upload was successful.
        load(file.name);

    } catch (e) {
        const errorMessage = e?.message || t('Error uploading cover.');
        toast.error(errorMessage);
        error(t('Upload failed'));

    } finally {
        setIsCoverUploading(false);
    }

    return { abort: () => { abort(); } };
  };

  // --- UPLOAD HANDLER FOR THE PDF FILEPOND ---
  const processBookPdf = async (fieldName, file, metadata, load, error, progress, abort) => {
  
    // Client-side validation before starting the stream
    if (file.type !== 'application/pdf') {
      const msg = t('InvalidFileTypePDF');
      toast.error(msg); error(msg); setIsPdfUploading(false); return;
    }
    if (file.size > MAX_FILE_SIZE) {
      const msg = t('FileSizeLimitExceeded50');
      toast.error(msg); error(msg); setIsPdfUploading(false); return;
    }

    setIsPdfUploading(true);
    setUploadLogs([]); // Clear previous logs
    setIsLogModalOpen(true);
    setIsLogModalMinimized(false);

    // Use a promise to bridge SSE with FilePond's callback structure
    try {
      await new Promise((resolve, reject) => {
        const onProgress = ({ event, data }) => {
          const parsedData = typeof data === "string" ? JSON.parse(data) : data;
          // Log every message from the stream
          if(data.message) {
            setUploadLogs(prevLogs => [...prevLogs, data.message]);
          }

          if (event === "file_ocr_done") {
            const data = {
              DocumentID: parsedData.DBID,
              OriginFileName: parsedData.text?.originalFileName,   
              Tags: parsedData.text?.tags
            }
            dispatch(updateDocument(data))
            .unwrap()
            .then(() => {
              navigate('/ai/book');
            })
            .catch((error) => {
              console.error("Metadata save failed:", error);
            });
          }

          // ** KEY LOGIC **
          // When 'uploaded' event is received, enable the form for submission
          // if (event === "uploaded") {
          //   if (data.uploadUrl) {
          //     // toast.success(t("File URL received. You can now save the form."));
          //     // setUploadedPdfData(data); 
          //     setIsPdfUploading(false);
          //     formik.setFieldValue('uploadedPdfData', data);
          //   } else {
          //     const err = new Error(t("URL not received from server."));
          //     toast.error(err.message);
          //     reject(err); // Reject the promise to fail the upload in FilePond
          //   }
          // }
            
          // If the stream reports a file is a duplicate
          if (event === "duplicate") {
            const err = new Error("duplicateFile");
            toast.error("duplicateFile");
            reject(err);
            return;
          }

          if (event === "other_data") {
            navigate('/ai/book');
          }

          // When the entire pipeline is finished, resolve the promise
          if (event === "all_done" || event === "done") {

            toggleLogModal();
            if (event === "done" && data.success === false) {
              const err = new Error(data.message || "Processing failed after upload.");
              toast.error(err.message);
              reject(err);
            } else {
              setIsLogModalOpen(false)
              toast.success(t("BookSavedSuccessfully"));     
              formik.setFieldValue('uploadedPdfData', file);
              resolve(); 
            }
          }
        };

        const details = JSON.stringify({
          name: formik.values.name,
          author: formik.values.author,
          publisher: formik.values.publisher,
          year: formik.values.year,
        })

        const subCategoryIds = formik.values.subCategory?.map(sc => sc.value) || [];
          
        // Prepare FormData for the initial POST request
        const formData = new FormData();
        formData.append("file", file);
        formData.append("Category", parentCategories[0]?.NameEn || "");
        formData.append("SubCategory", Array.isArray(formik.values.subCategory)
          ? formik.values.subCategory.map(subCat => subCat.SubCategory || "").join(", ")
          : ""
        );
        formData.append("SubCategoryIDs", JSON.stringify(subCategoryIds));
        formData.append("IsBook", true);
        // formData.append("IsLargeFile", false);
        formData.append("UserID", user.ID);
        formData.append("DocumentTypeID", 5);
        formData.append("Author", formik.values.author || "");
        formData.append("Bookname", formik.values.name || "");
        formData.append("ThumbnailURL", formik.values.uploadedCoverUrl);
        formData.append("IsVisible", formik.values.isPublic || "");
        formData.append("Details", details);
        formData.append("SaveJSON", false);

        // Dispatch the thunk that handles the SSE connection
        // This thunk MUST be implemented to accept an `onProgress` callback
        dispatch(uploadDocumentFile({ formData, onProgress }));
      });

      // This code runs if the promise above resolves successfully
      // toast.success(t('Document processing pipeline finished.'));
      load(file.name); // Tell FilePond the upload is complete

    } catch (e) {
      // This code runs if the promise is rejected
      error(e.message || "An unknown error occurred during processing.");
    } finally {
      setIsPdfUploading(false);
    }

    return { abort: () => { abort(); setIsPdfUploading(false); } };
  };



  const handleUpload = () => { setSubmitAction('upload'); formik.submitForm(); };

  const handleAddAnother = () => { 
    // setSubmitAction('addAnother'); 
    // formik.submitForm(); 

    formik.setFieldValue('uploadedCoverUrl', null);
    formik.setFieldValue('uploadedPdfData', null);

    if (filePondPdfRef.current) {
      filePondPdfRef.current.removeFiles();
    }
    if (filePondImageRef.current) {
      filePondImageRef.current.removeFiles();
    }

  };


  useEffect(() => {
    dispatch(fetchParentCategories({DocType: 5})); 
  }, [dispatch]);

    // --- NEW: FETCH SUB-CATEGORIES WHEN PARENT CATEGORY CHANGES ---
  useEffect(() => {
      // dispatch(fetchSubCategories({DocType: 4, ParentCategoryID: parentId })); 
      dispatch(fetchSubCategories({DocType: 5 })); 
  }, [dispatch]);

  // --- PREPARE OPTIONS FOR REACT-SELECT ---
  // const parentCategoriesOptions = parentCategories.map(cat => ({
  //   value: cat.ID,
  //   label: isRTL ? cat.NameAr.trim() : cat.NameEn.trim(),
  //   Category: cat.NameEn?.trim() || '',
  // }));

  const subCategoriesOptions = subCategories.map(cat => ({
    value: cat.ID,
    label: isRTL ? cat.CategoryNameAr.trim() : cat.CategoryNameEn.trim(),
    SubCategory: cat.CategoryNameEn?.trim() || '',
  }));

  return (
    <div className="page-content">
      <UploadProgressModal
        isOpen={isLogModalOpen}
        isMinimized={isLogModalMinimized}
        toggleMinimize={toggleLogModalMinimize}
        logs={uploadLogs}
        onClose={() => setIsLogModalOpen(false)}
        progress={uploadProgress}
        isRTL={isRTL}
      />
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col>
            <h4 className="mb-0">{t("Add New Book")}</h4>
          </Col>
          <Col xs="auto">
            {/* Go Back Button */}
            <Button color="soft-secondary" disabled={isPdfUploading} onClick={() => navigate(-1)}>
              <i className={`ri-arrow-${isRTL ? "right" : "left"}-line align-middle me-1`}></i> {t("Go Back")}
            </Button>
          </Col>
        </Row>

        <Form onSubmit={formik.handleSubmit}>
          <Row>
            {/* Main Book Details Column */}
            <Col lg={8}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{t("Book Details")}</h5>
                </CardHeader>
                <CardBody>
                   {/* ... form fields are unchanged ... */}
                  <Row>
                    <Col md={12}>
                      <FormGroup className="mb-3">
                        <Label htmlFor="book-name">{t("Name of the Book")}</Label>
                        <Input
                          type="text"
                          id="book-name"
                          name="name"
                          placeholder={t("Enter book name")}
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={isPdfUploading || formik.values.uploadedPdfData}
                          invalid={formik.touched.name && !!formik.errors.name}
                        />
                        {formik.touched.name && formik.errors.name ? (
                          <FormFeedback>{t(formik.errors.name, { defaultValue: "Book name is required" })}</FormFeedback>
                        ) : null}
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-3">
                        <Label htmlFor="author-name">{t("Author")}</Label>
                        <Input
                          type="text"
                          id="author-name"
                          name="author"
                          placeholder={t("Enter author name")}
                          value={formik.values.author}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={isPdfUploading || formik.values.uploadedPdfData}
                          invalid={formik.touched.author && !!formik.errors.author}
                        />
                        {formik.touched.author && formik.errors.author ? (
                          <FormFeedback>{t(formik.errors.author, { defaultValue: "Author name is required" })}</FormFeedback>
                        ) : null}
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-3">
                        <Label htmlFor="publisher-name">{t("Publisher")}</Label>
                        <Input
                          type="text"
                          id="publisher-name"
                          name="publisher"
                          placeholder={t("Enter publisher name")}
                          value={formik.values.publisher}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.publisher && !!formik.errors.publisher}
                        />
                        {formik.touched.publisher && formik.errors.publisher ? (
                          <FormFeedback>{t(formik.errors.publisher, { defaultValue: "Publisher name is required" })}</FormFeedback>
                        ) : null}
                      </FormGroup>  
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-3">
                        <Label htmlFor="publication-year">{t("Year")}</Label>
                        <Input
                          type="text"
                          id="publication-year"
                          name="year"
                          placeholder={t("e.g") + " 2024"}
                          value={formik.values.year}
                            onChange={
                            (e) => {
                              const { value } = e.target;
                              // Allow only digits and limit to 4 characters for a year.
                              if (/^\d{0,4}$/.test(value)) {
                                formik.setFieldValue('year', value);
                              }
                            }
                            } 
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.year && !!formik.errors.year}
                        />
                        {formik.touched.year && formik.errors.year ? (
                          <FormFeedback>{t(formik.errors.year, { defaultValue: "Publication year is required" })}</FormFeedback>
                        ) : null}
                      </FormGroup>
                    </Col>
                     {/* <Col md={6}>
                       <FormGroup className="mb-3">
                        <Label htmlFor="book-category">{t("Category")}</Label>
                         <Select
                          id="book-category"
                          name="category"
                          options={parentCategoriesOptions}
                          isLoading={categoriesLoading}
                          classNamePrefix="select"
                          className={formik.touched.category && formik.errors.category ? 'is-invalid' : ''}
                          value={formik.values.category}
                          onChange={option => formik.setFieldValue('category', option)}
                          onBlur={() => formik.setFieldTouched('category', true)}
                          placeholder={t("select")}
                          isDisabled={isPdfUploading || formik.values.uploadedPdfData} 
                          noOptionsMessage={() => t("NoOptions", { defaultValue: "No categories available" })}
                        />
                        {formik.touched.category && formik.errors.category ? (
                          <FormFeedback className="invalid-feedback d-block">{t(formik.errors.category, { defaultValue: "Category is required" })}</FormFeedback>
                        ) : null}
                      </FormGroup>
                    </Col> */}
                    {/* <Col md={6}>
                       <FormGroup className="mb-3">
                        <Label htmlFor="book-subcategory">{t("Sub-category")}</Label>
                         <Select
                          id="book-subcategory" name="subCategory"
                          options={subCategoriesOptions}
                          isLoading={subCategoriesLoading} 
                          isDisabled={subCategoriesLoading || isPdfUploading || formik.values.uploadedPdfData} 
                          value={formik.values.subCategory}
                          onChange={option => formik.setFieldValue('subCategory', option)}
                          onBlur={() => formik.setFieldTouched('subCategory', true)}
                          classNamePrefix="select"
                          className={formik.touched.subCategory && formik.errors.subCategory ? 'is-invalid' : ''}
                          placeholder={t("select")}
                          noOptionsMessage={() => t("NoOptions", { defaultValue: "No sub-categories available" })}
                        />
                        {formik.touched.subCategory && formik.errors.subCategory && <FormFeedback className="invalid-feedback d-block">{t(formik.errors.subCategory, { defaultValue: "Sub category is required" })}</FormFeedback>}
                      </FormGroup>
                    </Col> */}
                    <Col md={6}>
                      <FormGroup className="mb-3">
                        <Label htmlFor="book-subcategory">{t("Sub-category")}</Label>
                          <Select
                          id="book-subcategory"
                          name="subCategory"
                          isMulti
                          options={subCategoriesOptions}
                          value={formik.values.subCategory}
                          onChange={selectedOptions => formik.setFieldValue('subCategory', selectedOptions || [])}
                          onBlur={() => formik.setFieldTouched('subCategory', true)}
                          classNamePrefix="select"
                          className={formik.touched.subCategory && formik.errors.subCategory ? 'is-invalid' : ''}
                          placeholder={t("SelectSub-Categories")}
                          styles={getCustomSelectStyles(
                            formik.touched.subCategory && !!formik.errors.subCategory,
                            isRTL
                          )}
                          isLoading={subCategoriesLoading}
                          isDisabled={subCategoriesLoading || isPdfUploading || formik.values.uploadedPdfData}
                          noOptionsMessage={() => t("NoOptions", { defaultValue: "No sub-categories available" })}
                        />
                        {formik.touched.subCategory && formik.errors.subCategory && (
                          <FormFeedback className="invalid-feedback d-block">
                            {t(formik.errors.subCategory, { defaultValue: "Sub-category is required" })}
                          </FormFeedback>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Publishing & Assets Column */}
            <Col lg={4}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{t("Assets & Publishing")}</h5>
                </CardHeader>
                <CardBody>
                   <FormGroup className="d-flex justify-content-between align-items-center mb-4">
                    <Label htmlFor="is-public-switch" className="mb-0">
                      {t("View on Website")}
                    </Label>
                    <Switch
                      id="is-public-switch"
                      onChange={checked => formik.setFieldValue('isPublic', checked)}
                      checked={formik.values.isPublic}
                      onColor="#0ab39c"
                      offColor="#f06548"
                      height={24}
                      width={48}
                      handleDiameter={20}
                    />
                   </FormGroup>
                  <hr />
                  <FormGroup>
                    <Label>{t("Book Cover Image")}</Label>
                    <FilePond
                      ref={filePondImageRef}
                      name="coverImage"
                      allowMultiple={false}
                      acceptedFileTypes={['image/png', 'image/jpeg']}
                      maxFileSize="5MB" // Add the file size limit
  
                      // Custom error messages for a better user experience
                      labelFileTypeNotAllowed={t('InvalidImageType', { defaultValue: 'Only PNG or JPG images are allowed' })}
                      labelMaxFileSizeExceeded={t('ImageSizeLimitExceeded', { defaultValue: 'Image is too large' })}
                      labelMaxFileSize={t('MaximumImageSizeIs', { defaultValue: 'Maximum file size is 5 MB' })}
                      labelFileProcessing={t('filepondProcessing', { defaultValue: 'Uploading' })}
                      labelFileProcessingComplete={t('filepondComplete', { defaultValue: 'Upload complete' })}
                      labelFileProcessingAborted={t('filepondAborted', { defaultValue: 'Upload cancelled' })}
                      labelFileProcessingError={t('filepondError', { defaultValue: 'Error during upload' })}
                      labelTapToCancel={t('filepondTapToCancel', { defaultValue: 'tap to cancel' })}
                      labelTapToRetry={t('filepondTapToRetry', { defaultValue: 'tap to retry' })}
                      server={{ process: processCoverImage }} // <-- Use the custom upload handler
                      onupdatefiles={fileItems => {
                        
                        if (fileItems.length === 0) {
                          // setUploadedPdfData(null);
                          formik.setFieldValue('uploadedCoverUrl', null);
                        }
                      }}
                      disabled={isCoverUploading}
                      labelIdle={`${t("Drag & Drop PDF or")} <span class="filepond--label-action">(${t("Browse")})</span>`}
                    />
                    {formik.touched.uploadedCoverUrl && formik.errors.uploadedCoverUrl && 
                      <div className="invalid-feedback d-block mt-n3 mb-2">{t(formik.errors.uploadedCoverUrl)}</div>}
                  </FormGroup>
                  <hr />
                  <FormGroup className="mb-4">
                    <Label>{t("Upload Book PDF")}</Label>
                    <FilePond
                      ref={filePondPdfRef}
                      name="bookPdf"
                      allowMultiple={false}
                      acceptedFileTypes={['application/pdf', '.pdf']}
                      maxFileSize={"50MB"}
                      server={{ process: processBookPdf }} 
                      disabled={
                        isPdfUploading ||
                        !formik.values.subCategory ||
                        !formik.values.author ||
                        !formik.values.name || 
                        !formik.values.uploadedCoverUrl
                      }
                      labelIdle={`${t("Drag & Drop PDF or")} <span class="filepond--label-action">(${t("Browse")})</span>`}
                      labelFileProcessing={t('filepondProcessing', { defaultValue: 'Uploading' })}
                      labelFileProcessingComplete={t('filepondComplete', { defaultValue: 'Upload complete' })}
                      labelFileProcessingAborted={t('filepondAborted', { defaultValue: 'Upload cancelled' })}
                      labelFileProcessingError={t('filepondError', { defaultValue: 'Error during upload' })}
                      labelTapToCancel={t('filepondTapToCancel', { defaultValue: 'tap to cancel' })}
                      labelTapToRetry={t('filepondTapToRetry', { defaultValue: 'tap to retry' })}
                    />
                    {formik.touched.uploadedPdfData && formik.errors.uploadedPdfData && 
                      <div className="invalid-feedback d-block mt-n3 mb-2">{typeof formik.errors.uploadedPdfData === 'string' ? t(formik.errors.uploadedPdfData) : t('PDF upload is required')}</div>}
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Updated Action Buttons */}
          <Row className="mt-3">
            <Col className="d-flex align-items-center">
              {/* <Button type="button" color="primary" onClick={handleUpload}  
                disabled={!formik.isValid || !formik.values.uploadedPdfData || formik.isSubmitting || isCoverUploading || isPdfUploading}>
                {formik.isSubmitting ? t('Saving') : t('Save Book')}
              </Button> */}
              {/* <Button 
                type="button"
                color="success" 
                className="ms-2 me-2" 
                onClick={handleAddAnother} 
                disabled={!formik.isValid || !formik.values.uploadedPdfData || formik.isSubmitting || isCoverUploading || isPdfUploading}
              >
                 {t("Add Another")}
              </Button>
              <Button 
                type="button" 
                color="light" 
                // className="ms-auto" 
                onClick={handleReset}
                disabled={formik.isSubmitting || isCoverUploading || isPdfUploading } 
              >
                {t("Reset")}
              </Button> */}
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default AddBook;